import jsPDF from "jspdf";
import type { MeasurePlatformReport } from "./measurePlatformReportCsv";

/** Minimal palette — light document, single accent, clear type hierarchy */
const C = {
  ink: [17, 24, 39] as [number, number, number],
  body: [51, 65, 85] as [number, number, number],
  muted: [100, 116, 139] as [number, number, number],
  faint: [203, 213, 225] as [number, number, number],
  line: [226, 232, 240] as [number, number, number],
  surface: [248, 250, 252] as [number, number, number],
  accent: [255, 77, 0] as [number, number, number],
  white: [255, 255, 255] as [number, number, number],
};

const MARGIN = 20;
const FOOTER_H = 14;

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const formatDateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const slugify = (title: string) =>
  title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

type StatItem = { label: string; value: string };
type HeroStat = { label: string; value: string };

class MinimalReportPdf {
  private readonly doc: jsPDF;
  private readonly pageWidth: number;
  private readonly pageHeight: number;
  private readonly contentWidth: number;
  private y = MARGIN;

  constructor() {
    this.doc = new jsPDF({ unit: "mm", format: "a4" });
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - MARGIN * 2;
  }

  private bottomLimit() {
    return this.pageHeight - FOOTER_H;
  }

  private newPageIfNeeded(h: number) {
    if (this.y + h > this.bottomLimit()) {
      this.doc.addPage();
      this.y = MARGIN;
    }
  }

  private drawRule(y?: number, inset = 0) {
    const yy = y ?? this.y;
    this.doc.setDrawColor(...C.line);
    this.doc.setLineWidth(0.15);
    this.doc.line(MARGIN + inset, yy, this.pageWidth - MARGIN - inset, yy);
  }

  private textMuted(text: string, x: number, y: number, size = 8) {
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(size);
    this.doc.setTextColor(...C.muted);
    this.doc.text(text, x, y);
  }

  private textBody(text: string, x: number, y: number, size = 9.5, maxWidth?: number) {
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(size);
    this.doc.setTextColor(...C.body);
    if (maxWidth) {
      const lines = this.doc.splitTextToSize(text, maxWidth);
      this.doc.text(lines, x, y);
      return lines.length * (size * 0.42);
    }
    this.doc.text(text, x, y);
    return size * 0.42;
  }

  drawHeader(report: MeasurePlatformReport) {
    // Accent mark
    this.doc.setFillColor(...C.accent);
    this.doc.rect(MARGIN, this.y, 1.2, 14, "F");

    const textX = MARGIN + 5;
    this.textMuted("MEASURE · INTERNAL", textX, this.y + 3.5, 7);

    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(20);
    this.doc.setTextColor(...C.ink);
    const titleLines = this.doc.splitTextToSize(report.title, this.contentWidth - 6);
    this.doc.text(titleLines, textX, this.y + 11);
    this.y += 11 + (titleLines.length - 1) * 7 + 6;

    this.drawRule();
    this.y += 6;

    const meta = [
      formatDateTime(report.generatedAt),
      `${formatDate(report.activityWindow.from)} – ${formatDate(report.activityWindow.to)}`,
    ];
    this.textMuted(meta.join("   ·   "), MARGIN, this.y, 8);
    this.y += 5;

    const scopeH = this.textBody(report.scope.description, MARGIN, this.y, 9, this.contentWidth);
    this.y += scopeH + 8;
  }

  drawHeroStats(stats: HeroStat[]) {
    if (stats.length === 0) return;

    const count = Math.min(stats.length, 3);
    const gap = 6;
    const colW = (this.contentWidth - gap * (count - 1)) / count;
    const blockH = 22;

    this.newPageIfNeeded(blockH + 4);
    this.doc.setFillColor(...C.surface);
    this.doc.roundedRect(MARGIN, this.y, this.contentWidth, blockH, 2, 2, "F");

    for (let i = 0; i < count; i++) {
      const x = MARGIN + i * (colW + gap) + 4;
      const s = stats[i];
      this.textMuted(s.label.toUpperCase(), x, this.y + 7, 6.5);
      this.doc.setFont("helvetica", "bold");
      this.doc.setTextColor(...C.ink);
      const valueLines = this.doc.splitTextToSize(s.value, colW - 8);
      const valueSize = valueLines[0].length > 14 ? 11 : 16;
      this.doc.setFontSize(valueSize);
      this.doc.text(valueLines.slice(0, 2), x, this.y + 15);
    }

    this.y += blockH + 10;
  }

  drawSectionLabel(title: string) {
    this.newPageIfNeeded(12);
    this.y += 2;
    this.textMuted(title.toUpperCase(), MARGIN, this.y, 7);
    this.y += 5;
  }

  drawStatRows(items: StatItem[]) {
    const rowH = 7.5;
    this.newPageIfNeeded(items.length * rowH + 4);

    items.forEach((item, i) => {
      this.newPageIfNeeded(rowH);
      const rowY = this.y;

      if (i > 0) {
        this.drawRule(rowY - 2.5);
      }

      this.textBody(item.label, MARGIN, rowY + 4, 9);
      this.doc.setFont("helvetica", "bold");
      this.doc.setFontSize(9.5);
      this.doc.setTextColor(...C.ink);
      const valueWidth = this.doc.getTextWidth(item.value);
      this.doc.text(item.value, this.pageWidth - MARGIN - valueWidth, rowY + 4);

      this.y += rowH;
    });

    this.y += 6;
  }

  drawCallout(lines: string[]) {
    this.newPageIfNeeded(lines.length * 5 + 12);
    const pad = 5;
    const innerW = this.contentWidth - pad * 2 - 3;

    let blockH = pad * 2;
    const wrapped: string[][] = [];
    lines.forEach((line, i) => {
      if (!line) {
        blockH += 2;
        wrapped.push([]);
        return;
      }
      const isTitle = i === 0;
      const w = this.doc.splitTextToSize(line, innerW);
      wrapped.push(w);
      blockH += w.length * (isTitle ? 4.6 : 4.2) + 1;
    });

    this.doc.setFillColor(...C.surface);
    this.doc.setDrawColor(...C.line);
    this.doc.setLineWidth(0.15);
    this.doc.roundedRect(MARGIN, this.y, this.contentWidth, blockH, 1.5, 1.5, "FD");

    this.doc.setFillColor(...C.accent);
    this.doc.rect(MARGIN, this.y, 0.8, blockH, "F");

    let ty = this.y + pad + 3;
    wrapped.forEach((w, i) => {
      if (w.length === 0) {
        ty += 2;
        return;
      }
      const isTitle = i === 0;
      this.doc.setFont("helvetica", isTitle ? "bold" : "normal");
      this.doc.setFontSize(isTitle ? 9 : 8.5);
      this.doc.setTextColor(...(isTitle ? C.ink : C.body));
      this.doc.text(w, MARGIN + pad + 3, ty);
      ty += w.length * (isTitle ? 4.6 : 4.2) + 1;
    });

    this.y += blockH + 8;
  }

  drawEngagementOrgTable(
    orgs: Extract<MeasurePlatformReport, { reportType: "engagement" }>["organizations"]
  ) {
    const headers = ["Organization", "Engagement", "Posts", "Reports", "Score"];
    const colWidths = [72, 28, 22, 24, 20];
    const headerH = 7;
    const rowH = 6.5;

    const drawTableHead = () => {
      let x = MARGIN;
      for (let i = 0; i < headers.length; i++) {
        this.textMuted(headers[i].toUpperCase(), x, this.y + 4, 6.5);
        x += colWidths[i];
      }
      this.y += headerH;
      this.drawRule();
      this.y += 2;
    };

    drawTableHead();

    if (orgs.length === 0) {
      this.textMuted("No organizations match the current filters.", MARGIN, this.y + 4, 9);
      this.y += 10;
      return;
    }

    orgs.forEach((org) => {
      if (this.y + rowH > this.bottomLimit()) {
        this.doc.addPage();
        this.y = MARGIN;
        drawTableHead();
      }

      const cells = [
        org.name,
        org.engagement,
        String(org.postsInWindow),
        String(org.reportsInWindow),
        String(org.activityScore),
      ];
      let x = MARGIN;
      for (let col = 0; col < cells.length; col++) {
        const isName = col === 0;
        this.doc.setFont("helvetica", isName ? "bold" : "normal");
        this.doc.setFontSize(isName ? 9 : 8.5);
        this.doc.setTextColor(...(isName ? C.ink : C.body));
        const clipped = this.doc.splitTextToSize(String(cells[col]), colWidths[col] - 2);
        this.doc.text(clipped[0] ?? "", x, this.y + 4);
        x += colWidths[col];
      }

      if (org.region || org.cohort) {
        const sub = [org.region, org.cohort ? `Cohort · ${org.cohort}` : null].filter(Boolean).join(" · ");
        this.textMuted(sub, MARGIN + 1, this.y + 8, 7);
        this.y += 3.5;
      }

      this.y += rowH;
      this.drawRule(this.y, 2);
      this.y += 2;
    });

    this.y += 4;
  }

  drawParticipationOrgTable(
    orgs: Extract<MeasurePlatformReport, { reportType: "participation" }>["organizations"]
  ) {
    const headers = ["Organization", "Members", "Events", "Posts", "Engagement"];
    const colWidths = [68, 24, 22, 22, 34];
    const headerH = 7;
    const rowH = 6.5;

    const drawTableHead = () => {
      let x = MARGIN;
      for (let i = 0; i < headers.length; i++) {
        this.textMuted(headers[i].toUpperCase(), x, this.y + 4, 6.5);
        x += colWidths[i];
      }
      this.y += headerH;
      this.drawRule();
      this.y += 2;
    };

    drawTableHead();

    if (orgs.length === 0) {
      this.textMuted("No organizations match the current filters.", MARGIN, this.y + 4, 9);
      this.y += 10;
      return;
    }

    orgs.forEach((org) => {
      if (this.y + rowH > this.bottomLimit()) {
        this.doc.addPage();
        this.y = MARGIN;
        drawTableHead();
      }

      const cells = [
        org.name,
        String(org.memberCount),
        String(org.eventsInWindow),
        String(org.postsInWindow),
        org.engagement,
      ];
      let x = MARGIN;
      for (let col = 0; col < cells.length; col++) {
        const isName = col === 0;
        this.doc.setFont("helvetica", isName ? "bold" : "normal");
        this.doc.setFontSize(isName ? 9 : 8.5);
        this.doc.setTextColor(...(isName ? C.ink : C.body));
        const clipped = this.doc.splitTextToSize(String(cells[col]), colWidths[col] - 2);
        this.doc.text(clipped[0] ?? "", x, this.y + 4);
        x += colWidths[col];
      }

      this.y += rowH;
      this.drawRule(this.y, 2);
      this.y += 2;
    });

    this.y += 4;
  }

  drawRecentEvents(
    events: Extract<MeasurePlatformReport, { reportType: "participation" }>["recentEvents"]
  ) {
    if (events.length === 0) {
      this.textMuted("No events in this activity window for the filtered organizations.", MARGIN, this.y + 4, 9);
      this.y += 10;
      return;
    }

    const rowH = 8;
    events.forEach((event) => {
      this.newPageIfNeeded(rowH + 4);
      this.doc.setFont("helvetica", "bold");
      this.doc.setFontSize(9);
      this.doc.setTextColor(...C.ink);
      const titleLines = this.doc.splitTextToSize(event.title, this.contentWidth - 4);
      this.doc.text(titleLines[0] ?? "", MARGIN, this.y + 4);

      const meta = `${event.eventType} · ${event.organizationName} · ${formatDate(event.startsAt)}`;
      this.textMuted(meta, MARGIN, this.y + 8, 7.5);

      this.y += rowH;
      this.drawRule(this.y, 2);
      this.y += 2;
    });

    this.y += 4;
  }

  drawImpactHighlights(
    highlights: Extract<MeasurePlatformReport, { reportType: "impact" }>["highlights"]
  ) {
    if (highlights.length === 0) {
      this.textMuted("No impact reports filed in this window for the filtered organizations.", MARGIN, this.y + 4, 9);
      this.y += 10;
      return;
    }

    highlights.forEach((item) => {
      const excerptLines = item.narrativeExcerpt
        ? this.doc.splitTextToSize(item.narrativeExcerpt, this.contentWidth - 14)
        : ["No narrative on file."];
      const blockH = 16 + excerptLines.length * 4;

      this.newPageIfNeeded(blockH);

      this.doc.setFont("helvetica", "bold");
      this.doc.setFontSize(9.5);
      this.doc.setTextColor(...C.ink);
      this.doc.text(item.organizationName, MARGIN, this.y + 4);

      const subParts = [
        item.funderName,
        item.grantName || null,
        `${formatDate(item.reportingPeriod.from)} - ${formatDate(item.reportingPeriod.to)}`,
      ].filter(Boolean);
      this.textMuted(subParts.join("   |   "), MARGIN, this.y + 9, 7.5);

      let ty = this.y + 13;
      excerptLines.forEach((line: string) => {
        this.textBody(line, MARGIN, ty, 8.5, this.contentWidth - 4);
        ty += 4;
      });

      this.y = ty + 4;
      this.drawRule(this.y, 2);
      this.y += 4;
    });
  }

  drawOrgTable(orgs: MeasurePlatformReport["organizations"]) {
    const headers = ["Organization", "Region", "Engagement", "Onboarding"];
    const colWidths = [68, 38, 32, 32];
    const headerH = 7;
    const rowH = 6.5;

    const drawTableHead = () => {
      let x = MARGIN;
      for (let i = 0; i < headers.length; i++) {
        this.textMuted(headers[i].toUpperCase(), x, this.y + 4, 6.5);
        x += colWidths[i];
      }
      this.y += headerH;
      this.drawRule();
      this.y += 2;
    };

    drawTableHead();

    if (orgs.length === 0) {
      this.textMuted("No organizations match the current filters.", MARGIN, this.y + 4, 9);
      this.y += 10;
      return;
    }

    orgs.forEach((org) => {
      if (this.y + rowH > this.bottomLimit()) {
        this.doc.addPage();
        this.y = MARGIN;
        drawTableHead();
      }

      const cells = [org.name, org.region, org.engagement, org.onboarding];
      let x = MARGIN;
      for (let col = 0; col < cells.length; col++) {
        const isName = col === 0;
        this.doc.setFont("helvetica", isName ? "bold" : "normal");
        this.doc.setFontSize(isName ? 9 : 8.5);
        this.doc.setTextColor(...(isName ? C.ink : C.body));
        const clipped = this.doc.splitTextToSize(String(cells[col]), colWidths[col] - 2);
        this.doc.text(clipped[0] ?? "", x, this.y + 4);
        x += colWidths[col];
      }

      if (org.cohort) {
        this.textMuted(`Cohort · ${org.cohort}`, MARGIN + 1, this.y + 8, 7);
        this.y += 3.5;
      }

      this.y += rowH;
      this.drawRule(this.y, 2);
      this.y += 2;
    });

    this.y += 4;
  }

  drawFooters() {
    const total = this.doc.getNumberOfPages();
    for (let p = 1; p <= total; p++) {
      this.doc.setPage(p);
      this.drawRule(this.pageHeight - FOOTER_H + 2);
      this.textMuted("Ignite Measure · Confidential", MARGIN, this.pageHeight - 6, 7);
      const pageLabel = `${p} / ${total}`;
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(7);
      this.doc.setTextColor(...C.muted);
      const w = this.doc.getTextWidth(pageLabel);
      this.doc.text(pageLabel, this.pageWidth - MARGIN - w, this.pageHeight - 6);
    }
  }

  save(filename: string) {
    this.drawFooters();
    this.doc.save(filename);
  }
}

const onboardingLine = (o: Record<string, number>) =>
  `Complete ${o.Complete ?? 0}  ·  In progress ${o["In progress"] ?? 0}  ·  Not started ${o["Not started"] ?? 0}`;

const engagementLine = (e: Record<string, number>) =>
  `High ${e.High ?? 0}  ·  Medium ${e.Medium ?? 0}  ·  Low ${e.Low ?? 0}`;

/** jsPDF standard fonts only support WinAnsi — keep callout copy ASCII-only. */
const engagementMethodologyLines = () => [
  "How engagement is scored in this activity window",
  "Score = posts + (impact reports x 2) + goals (up to 3)",
  "High: 5+   Medium: 2-4   Low: under 2",
  "Participation = share of orgs with Medium or High engagement",
];

export const downloadMeasurePlatformReportPdf = (report: MeasurePlatformReport) => {
  const pdf = new MinimalReportPdf();
  pdf.drawHeader(report);

  if (report.reportType === "engagement") {
    const reach = report.platformReach;
    const p = report.portfolio;

    pdf.drawHeroStats([
      { label: "Organizations in scope", value: p.organizationCount.toLocaleString() },
      { label: "Participation", value: `${p.participationIndex}%` },
      { label: "Active users (30d)", value: reach.activeUsers30.toLocaleString() },
    ]);

    pdf.drawCallout(engagementMethodologyLines());

    pdf.drawSectionLabel("Platform reach");
    pdf.drawStatRows([
      { label: "Active users (60 / 90 days)", value: `${reach.activeUsers60.toLocaleString()} / ${reach.activeUsers90.toLocaleString()}` },
      { label: "Posts (all time)", value: reach.totalPosts.toLocaleString() },
      { label: "Interactions (all time)", value: reach.totalInteractions.toLocaleString() },
    ]);

    pdf.drawSectionLabel("Activity in scope");
    pdf.drawStatRows([
      { label: "Posts in window", value: p.postsInWindow.toLocaleString() },
      { label: "Report activity in window", value: p.orgReportsInWindow.toLocaleString() },
      { label: "Inactive organizations", value: p.inactive.toLocaleString() },
      { label: "Needs support", value: p.needsSupport.toLocaleString() },
      { label: "Engagement mix", value: engagementLine(p.engagement) },
    ]);

    const topRegions = report.byRegion.slice(0, 5);
    if (topRegions.length > 0) {
      pdf.drawSectionLabel("Top regions");
      pdf.drawStatRows(
        topRegions.map((r) => ({
          label: r.label,
          value: `${r.orgCount} orgs · ${r.participationIndex}% · H${r.high} M${r.medium} L${r.low}`,
        }))
      );
    }

    if (report.cohortParticipation.length > 0) {
      pdf.drawSectionLabel("Cohort participation");
      pdf.drawStatRows(
        report.cohortParticipation.slice(0, 6).map((c) => ({
          label: c.name,
          value: `${c.orgCount} orgs · ${c.participationIndex}%`,
        }))
      );
    }

    pdf.drawSectionLabel(`Organizations (${report.organizations.length})`);
    pdf.drawEngagementOrgTable(report.organizations);
  } else if (report.reportType === "participation") {
    const totals = report.platformTotals;
    const p = report.portfolio;

    pdf.drawHeroStats([
      { label: "Organizations in scope", value: p.organizationCount.toLocaleString() },
      { label: "Participation", value: `${p.participationIndex}%` },
      { label: "Events in window", value: totals.eventsInWindow.toLocaleString() },
    ]);

    pdf.drawCallout([
      "Participation summary",
      "Events, member counts, and cohort participation for filtered organizations.",
      "Participation index = orgs with Medium or High engagement in the window.",
    ]);

    pdf.drawSectionLabel("Participation totals");
    pdf.drawStatRows([
      { label: "Members in scope", value: totals.totalMembersInScope.toLocaleString() },
      { label: "Active users (30d)", value: totals.activeUsers30.toLocaleString() },
      { label: "Posts in window", value: totals.postsInWindow.toLocaleString() },
      { label: "Impact report activity in window", value: totals.orgReportsInWindow.toLocaleString() },
      {
        label: "Events (all time on platform)",
        value: (totals.eventsAllTime ?? 0).toLocaleString(),
      },
    ]);

    if (report.eventsByType.length > 0) {
      pdf.drawSectionLabel("Events by type");
      pdf.drawStatRows(
        report.eventsByType.map((e) => ({
          label: e.eventType,
          value: e.count.toLocaleString(),
        }))
      );
    }

    if (report.cohortParticipation.length > 0) {
      pdf.drawSectionLabel("Cohort participation");
      pdf.drawStatRows(
        report.cohortParticipation.slice(0, 6).map((c) => ({
          label: c.name,
          value: `${c.orgCount} orgs · ${c.participationIndex}%`,
        }))
      );
    }

    if (report.recentEvents.length > 0) {
      pdf.drawSectionLabel(`Recent events (${report.recentEvents.length})`);
      pdf.drawRecentEvents(report.recentEvents);
    }

    pdf.drawSectionLabel(`Organizations (${report.organizations.length})`);
    pdf.drawParticipationOrgTable(report.organizations);
  } else if (report.reportType === "impact") {
    const p = report.portfolio;

    pdf.drawHeroStats([
      { label: "Impact reports in window", value: report.impactReportsInWindow.toLocaleString() },
      { label: "Organizations in scope", value: p.organizationCount.toLocaleString() },
      { label: "All-time reports", value: report.impactReportsAllTime.toLocaleString() },
    ]);

    pdf.drawSectionLabel("Portfolio in scope");
    pdf.drawStatRows([
      { label: "Participation index", value: `${p.participationIndex}%` },
      { label: "Posts in window", value: p.postsInWindow.toLocaleString() },
      { label: "Report activity in window", value: p.orgReportsInWindow.toLocaleString() },
    ]);

    if (report.byFunder.length > 0) {
      pdf.drawSectionLabel("Impact reports by funder");
      pdf.drawStatRows(
        report.byFunder.slice(0, 8).map((f) => ({
          label: f.funder,
          value: f.count.toLocaleString(),
        }))
      );
    }

    pdf.drawSectionLabel(`Impact highlights (${report.highlights.length})`);
    pdf.drawImpactHighlights(report.highlights);

    pdf.drawSectionLabel(`Organizations (${report.organizations.length})`);
    pdf.drawOrgTable(report.organizations);
  } else if (report.reportType === "executive-summary") {
    const m = report.platformSnapshot;
    const p = report.portfolio;

    pdf.drawHeroStats([
      { label: "Organizations in scope", value: p.organizationCount.toLocaleString() },
      { label: "Participation", value: `${p.participationIndex}%` },
      { label: "Active users (30d)", value: m.activeUsers30.toLocaleString() },
    ]);

    pdf.drawSectionLabel("Platform");
    pdf.drawStatRows([
      { label: "Total organizations", value: m.totalOrganizations.toLocaleString() },
      { label: "Total users", value: m.totalUsers.toLocaleString() },
      { label: "Active users (60 / 90 days)", value: `${m.activeUsers60.toLocaleString()} / ${m.activeUsers90.toLocaleString()}` },
      { label: "New users this month", value: m.newUsersThisMonth.toLocaleString() },
      { label: "Posts & interactions", value: `${m.totalPosts.toLocaleString()} / ${m.totalInteractions.toLocaleString()}` },
      { label: "Impact reports (all time)", value: m.reportsGenerated.toLocaleString() },
    ]);

    pdf.drawSectionLabel("Portfolio in scope");
    pdf.drawStatRows([
      { label: "Inactive", value: p.inactive.toLocaleString() },
      { label: "Needs support", value: p.needsSupport.toLocaleString() },
      { label: "Posts in window", value: p.postsInWindow.toLocaleString() },
      { label: "Report activity in window", value: p.orgReportsInWindow.toLocaleString() },
      { label: "Onboarding", value: onboardingLine(p.onboarding) },
      { label: "Engagement", value: engagementLine(p.engagement) },
    ]);

    if (report.funder.selectedFunder) {
      pdf.drawSectionLabel("Funder");
      const lines = [
        report.funder.selectedFunder,
        `${report.funder.impactReportsInWindow ?? 0} impact reports in this activity window`,
      ];
      if (report.funder.portfolioParticipation) {
        lines.push(
          `${report.funder.portfolioParticipation.orgCount} orgs on platform · ${report.funder.portfolioParticipation.participationIndex}% participation`
        );
      }
      pdf.drawCallout(lines);
    }
  } else if (report.reportType === "funder") {
    const f = report.funder;
    const p = report.portfolioInScope;

    pdf.drawHeroStats([
      { label: "Grant portfolio", value: f.name },
      { label: "Orgs on platform", value: f.organizationsOnPlatform.toLocaleString() },
      { label: "Participation", value: `${f.platformParticipationIndex}%` },
    ]);

    pdf.drawCallout([
      `${f.impactReportsInWindow} impact reports filed to this funder in the activity window`,
      `${p.organizationCount} organizations in the filtered portfolio`,
    ]);

    pdf.drawSectionLabel("Filtered portfolio");
    pdf.drawStatRows([
      { label: "Participation index", value: `${p.participationIndex}%` },
      { label: "Posts in window", value: p.postsInWindow.toLocaleString() },
      { label: "Report activity in window", value: p.orgReportsInWindow.toLocaleString() },
      { label: "Onboarding", value: onboardingLine(p.onboarding) },
      { label: "Engagement", value: engagementLine(p.engagement) },
    ]);
  }

  if (report.reportType === "executive-summary" || report.reportType === "funder") {
    pdf.drawSectionLabel(`Organizations (${report.organizations.length})`);
    pdf.drawOrgTable(report.organizations);
  }

  pdf.save(`${slugify(report.title) || "ignite-report"}-${new Date().toISOString().slice(0, 10)}.pdf`);
};
